import { type MutableRefObject, useCallback, useState } from 'react'

import { type FormikHelpers } from 'formik'

import { type ExpenseReportFormSchema } from '@webapp/modules/expense/components/CreateExpenseReportForm'

type BudgetConfirmationState =
  | { status: 'closed' }
  | {
      status: 'open'
      values: ExpenseReportFormSchema
      actions: FormikHelpers<ExpenseReportFormSchema>
    }

const CLOSED: BudgetConfirmationState = { status: 'closed' }

export const useBudgetConfirmation = (
  submitReportRef: MutableRefObject<
    (
      values: ExpenseReportFormSchema,
      actions: FormikHelpers<ExpenseReportFormSchema>,
      shouldSkipBudgetCheck: boolean
    ) => Promise<void>
  >
) => {
  const [state, setState] = useState<BudgetConfirmationState>(CLOSED)

  const handleBudgetError = useCallback(
    (
      formValues: ExpenseReportFormSchema,
      formActions: FormikHelpers<ExpenseReportFormSchema>
    ) => {
      setState({ status: 'open', values: formValues, actions: formActions })
    },
    []
  )

  const handleConfirmOverBudget = useCallback(
    async (done: () => void) => {
      try {
        if (state.status === 'open') {
          await submitReportRef.current(state.values, state.actions, true)
        }
      } finally {
        setState(CLOSED)
        done()
      }
    },
    [state, submitReportRef]
  )

  const handleDismissBudgetConfirm = useCallback(() => {
    setState(CLOSED)
  }, [])

  return {
    isBudgetConfirmOpen: state.status === 'open',
    onBudgetError: handleBudgetError,
    onConfirmOverBudget: handleConfirmOverBudget,
    onDismissBudgetConfirm: handleDismissBudgetConfirm,
  }
}
